// M2E.D: Loop receives ANY renderable manifest item (MEDIA or template)
// and dispatches internally to ItemView (MEDIA) or TemplateView
// (MENU_BOARD / PROMO). The two-layer crossfade pattern is unchanged.
import { useEffect, useRef, useState } from 'react';
import { ItemView } from './ItemView';
import { TemplateView } from '../templates';
import { FADE_TRANSITION_MS } from '../config';
import type {
  ManifestItem,
  ManifestMediaItem,
  ManifestMenuBoardItem,
  ManifestPromoItem,
} from '../types';

interface LoopProps {
  item: ManifestItem;
  onAdvance: () => void;
}

interface Layer {
  item: ManifestItem;
  key: number;
}

interface LayerState {
  current: Layer;
  previous: Layer | null;
}

// Two-layer crossfade orchestrator. When the `item` prop changes, the
// previous layer is kept in the DOM at opacity 0 for FADE_TRANSITION_MS
// while the new layer fades from 0 to 1. Only the current layer drives
// `onAdvance`; the outgoing layer is inert.
//
// Single state for both layers ensures atomic update — there is never a
// moment where `previous` and `current` reference the same item.
export function Loop({ item, onAdvance }: LoopProps) {
  const [layers, setLayers] = useState<LayerState>({
    current: { item, key: 0 },
    previous: null,
  });
  const lastItemIdRef = useRef(item.id);
  const keyRef = useRef(0);

  useEffect(() => {
    // Initial mount — `item` matches the seed state, no transition needed.
    if (lastItemIdRef.current === item.id) return;
    lastItemIdRef.current = item.id;
    keyRef.current += 1;
    const newKey = keyRef.current;

    setLayers((prev) => ({
      previous: prev.current,
      current: { item, key: newKey },
    }));

    // Clear `previous` only if we are still the most recent transition.
    // If another item change races in, its own timeout will handle cleanup.
    const t = window.setTimeout(() => {
      setLayers((prev) =>
        prev.current.key === newKey ? { ...prev, previous: null } : prev,
      );
    }, FADE_TRANSITION_MS);

    return () => clearTimeout(t);
  }, [item.id, item]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: '#000',
        overflow: 'hidden',
      }}
    >
      {layers.previous && (
        <LayerContent
          key={layers.previous.key}
          item={layers.previous.item}
          fadingOut
          onEnd={() => undefined}
        />
      )}
      <LayerContent
        key={layers.current.key}
        item={layers.current.item}
        onEnd={onAdvance}
      />
    </div>
  );
}

// Routes an item to its renderer:
//   - MEDIA (or legacy items lacking itemKind) -> ItemView (image/video)
//   - MENU_BOARD / PROMO -> TemplateView (registry dispatch)
//
// Player.tsx's isRenderableItem filter guarantees we never receive an
// item whose kind isn't handled here.
function LayerContent({
  item,
  fadingOut = false,
  onEnd,
}: {
  item: ManifestItem;
  fadingOut?: boolean;
  onEnd: () => void;
}) {
  const kind = (item as { itemKind?: string }).itemKind;
  if (kind === undefined || kind === 'MEDIA') {
    return (
      <ItemView
        item={item as ManifestMediaItem}
        fadingOut={fadingOut}
        onEnd={onEnd}
      />
    );
  }
  return (
    <TemplateView
      item={item as ManifestMenuBoardItem | ManifestPromoItem}
      fadingOut={fadingOut}
      onEnd={onEnd}
    />
  );
}
